/**
 * 调用日志的筛选条件 → 后端 query。纯逻辑，可单测。
 *
 * 两条规则值得写在这里而不是页面里：
 *
 * 1. **默认带时间窗。** `llm_call_log` 是按月 RANGE 分区的大表，没有窗口的默认首屏就是从整张表的
 *    头上往下翻。所以进页面默认是「最近 24 小时」，人自己要往前翻才放宽。
 *
 * 2. **按 request_id 查的时候不带时间窗。** 它走的是 `uk_request` 唯一键，一次调用就一行；
 *    这时候还叠一个「最近 24 小时」，只会让人拿着一个真实存在的 request_id 却查不到东西。
 */

/** 时间窗的档位。`custom` 由页面给出起止时刻。 */
export const TIME_WINDOWS = ['1h', '24h', '7d', '30d', 'custom']

/**
 * key 筛选里「还没租到密钥就失败」那一档（`key_id IS NULL`）—— 池子里没有可用 key 或配置无效，
 * 请求根本没发出去。它是后端认的一个取值，不是「没选」。空串才是没选。
 */
export const NO_KEY = 'none'

const WINDOW_MS = {
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
}

/**
 * @typedef {object} CallFilters
 * @property {string} window        TIME_WINDOWS 之一
 * @property {string} since         `custom` 时的起始（datetime-local 的值），其余档位忽略
 * @property {string} until         `custom` 时的结束（datetime-local 的值），其余档位忽略
 * @property {string} provider
 * @property {string} model
 * @property {string} status        '' | 'ok' | 'error' | 'aborted'
 * @property {string} userId
 * @property {string} sessionId
 * @property {string} requestId
 * @property {string} keyId         key 的 id；`NO_KEY` = 还没租到密钥就失败的那一类；空串 = 不筛
 * @property {string} capability
 * @property {string} billingMode
 * @property {string} costState
 * @property {string} providerRequestId
 * @property {string} source
 * @property {string} business_status
 * @property {string} story_task
 * @property {string} story_query
 */

/** @returns {CallFilters} */
export function emptyCallFilters() {
  return {
    window: '24h',
    since: '',
    until: '',
    provider: '',
    model: '',
    status: '',
    userId: '',
    sessionId: '',
    requestId: '',
    keyId: '',
    capability: 'text',
    billingMode: '',
    costState: 'all',
    providerRequestId: '',
    source: '',
    business_status: '',
    story_task: '',
    story_query: '',
  }
}

/**
 * URL query → 筛选条件。**别的页面就是靠这个跳过来的**：
 *
 *   成本看板下钻某一天的某个模型 → `/llm/calls?since=…&until=…&capability=…&model=…`
 *   对账页定位某一条调用        → `/llm/calls?request_id=…`
 *
 * 参数名与后端 `ListCallsQuery` 的**原生字段名**一致（`since` / `until` / `capability` / `model` /
 * `status` / `user_id` / `session_id` / `request_id` / `key_id`），跳转方不需要认识本页的内部字段名。
 * 运行时面板就是靠 `key_id` 跳过来的：`/llm/calls?key_id=7&status=error` = 「这把 key 失败的那些调用」。
 *
 * 带了 `since` / `until` 就说明来人指定了一个具体区间 —— 时间窗切到 `custom`，**不能继续用
 * 默认的「最近 24 小时」**，否则人跳过来看到的是另一段时间的数据，还以为筛选丢了。
 *
 * @param {Record<string, string|string[]>} query  `useRoute().query`
 * @returns {CallFilters}
 */
export function callFiltersFromQuery(query) {
  const filters = emptyCallFilters()
  const one = (key) => queryValue(query, key)
  filters.provider = one('provider')
  filters.model = one('model')
  filters.status = one('status')
  filters.userId = one('user_id')
  filters.sessionId = one('session_id')
  filters.requestId = one('request_id')
  filters.keyId = one('key_id')
  filters.capability = one('capability') || 'text'
  filters.billingMode = one('billing_mode')
  filters.costState = one('cost_state') || 'all'
  filters.providerRequestId = one('provider_request_id')
  for (const key of ['source', 'business_status', 'story_task', 'story_query']) filters[key] = one(key)
  if ((filters.model === '') !== (filters.provider === '')) {
    throw new Error('callFiltersFromQuery: model 与 provider 必须同时存在')
  }

  const since = one('since')
  const until = one('until')
  if (since || until) {
    filters.window = 'custom'
    filters.since = isoToLocal(since)
    filters.until = isoToLocal(until)
  }
  return filters
}

function queryValue(query, key) {
  const value = query[key]
  if (value === undefined) return ''
  if (typeof value !== 'string') throw new Error(`callFiltersFromQuery: ${key} 必须只有一个字符串值`)
  return value
}

/**
 * 筛选条件 → 后端 query。空串一律不发（后端把缺省当「不筛」）。
 * @param {CallFilters} filters
 * @param {{ limit: number, offset: number, now?: Date }} page
 */
export function buildCallQuery(filters, page) {
  if ((filters.model === '') !== (filters.provider === '')) {
    throw new Error('buildCallQuery: model 与 provider 必须同时存在')
  }
  const query = { limit: page.limit, offset: page.offset, capability: filters.capability, cost_state: filters.costState }
  if (filters.model) {
    query.provider = filters.provider
    query.model = filters.model
  }
  if (filters.status) query.status = filters.status
  if (filters.userId) query.user_id = filters.userId
  if (filters.sessionId) query.session_id = filters.sessionId
  // 后端走 idx_key_time：数字是那把 key，`none` 是「还没租到密钥就失败」的那一类。
  if (filters.keyId) query.key_id = filters.keyId
  if (filters.billingMode) query.billing_mode = filters.billingMode
  if (filters.providerRequestId) query.provider_request_id = filters.providerRequestId
  if (filters.capability === 'text') {
    for (const key of ['source', 'business_status', 'story_task', 'story_query']) {
      if (filters[key]) query[key] = filters[key]
    }
  }
  if (filters.requestId) {
    // 唯一键查询，不叠时间窗（见文件头第 2 条）。
    query.call_id = filters.requestId
    return query
  }
  const range = timeRange(filters, page.now ?? new Date())
  if (range.since) query.since = range.since
  if (range.until) query.until = range.until
  return query
}

/**
 * 时间窗 → ISO 起止。`custom` 两头都可以空（只填一头就是单边）。
 * @param {CallFilters} filters
 * @param {Date} now
 * @returns {{ since: string|null, until: string|null }}
 */
export function timeRange(filters, now) {
  if (filters.window === 'custom') {
    return { since: localToIso(filters.since), until: localToIso(filters.until) }
  }
  const span = WINDOW_MS[filters.window]
  // 档位是从 TIME_WINDOWS 里选出来的，选出别的值说明页面坏了 —— 抛错，不兜一个默认窗口。
  if (span === undefined) throw new Error(`buildCallQuery: unknown time window '${filters.window}'`)
  return { since: new Date(now.getTime() - span).toISOString(), until: null }
}

/**
 * `<input type="datetime-local">` 的值（本地时刻，无时区）→ ISO。空串就是没填。
 * @param {string} local
 * @returns {string|null}
 */
function localToIso(local) {
  if (!local) return null
  const at = new Date(local)
  if (Number.isNaN(at.getTime())) throw new Error(`buildCallQuery: invalid datetime '${local}'`)
  return at.toISOString()
}

/**
 * ISO → `<input type="datetime-local">` 的值。**按本地时区折算**，不是把 ISO 前 16 个字符截下来 ——
 * 那样在 UTC+8 会把 00:00 显示成 00:00 而实际查的是 08:00，差整整八小时。
 * @param {string} iso
 * @returns {string}
 */
function isoToLocal(iso) {
  if (!iso) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) throw new Error(`callFiltersFromQuery: invalid datetime '${iso}'`)
  const pad = (n) => String(n).padStart(2, '0')
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`
}
